/**
 * PRS Comparison Viewer Component
 * Displays PRS comparison in structured, expandable sections
 *
 * Shows current_state vs changes in a table format:
 * - Schedule section (visits with config changes, forms)
 * - App Functionalities section
 * - Custom Requirements section
 */

import React, {useState} from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

interface PRSComparisonViewerProps {
  comparisonData: any; // Full comparison JSON from API
}

export const PRSComparisonViewer: React.FC<PRSComparisonViewerProps> = ({comparisonData}) => {
  const [expandedSections, setExpandedSections] = useState<string[]>(['schedule']);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [selectedContext, setSelectedContext] = useState<{request: string; context: string} | null>(null);

  // Extract actual comparison data (API wraps it in a response object)
  // If comparison is a string, parse it to JSON
  let comparison = comparisonData?.comparison || comparisonData;

  if (typeof comparison === 'string') {
    try {
      comparison = JSON.parse(comparison);
    } catch (e) {
      console.error('Failed to parse comparison:', e);
    }
  }


  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const formatConfigKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const getVisitConfigChanges = (visit: any): Record<string, {old: string; new: string}> => {
    return visit.web_config_changes || visit.visit_config_changes || visit.app_config_changes || {};
  };

  const getVisitConfig = (visit: any): Record<string, string> => {
    return visit.web_config || visit.visit_config || visit.app_config || {};
  };

  const countScheduleChanges = () => {
    if (!comparison?.changes?.schedule) return 0;

    const schedule = comparison.changes.schedule;
    let count = 0;

    if (schedule.visits) {
      count += schedule.visits.add?.length || 0;
      count += schedule.visits.remove?.length || 0;
      count += schedule.visits.modify?.length || 0;
    }

    if (schedule.forms) {
      count += schedule.forms.add?.length || 0;
      count += schedule.forms.remove?.length || 0;
      count += schedule.forms.modify?.length || 0;
    }

    return count;
  };

  // Find current state for a visit
  const getCurrentVisit = (visitId: string) => {
    const currentVisits = comparison?.current_state?.schedule?.visits || [];
    return currentVisits.find((v: any) => v.visit_id === visitId);
  };

  // Render a single form
  const renderFormCell = (form: any, type: 'current' | 'new' | 'added' | 'removed' | 'modified' | 'unchanged') => {
    if (!form) {
      return <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>;
    }

    const isRemoved = type === 'removed';

    return (
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 1,
            textDecoration: isRemoved ? 'line-through' : 'none',
            color: isRemoved ? 'error.main' : 'inherit',
          }}
        >
          {form.name || form.gl_form_name || '---'}
        </Typography>
        {form.type && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Type: {form.type}
          </Typography>
        )}
        {form.administrator && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Administrator: {form.administrator}
          </Typography>
        )}
        {form.timing_rule && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Timing: {form.timing_rule}
          </Typography>
        )}
        {form.gl_form_name && (
          <Typography variant="body2" color="text.secondary">
            Global Library: {form.gl_form_name}
          </Typography>
        )}
      </Box>
    );
  };

  const renderConfigChangesTable = (
    configChanges: Record<string, {old: string; new: string}>,
    currentConfig: Record<string, string>,
  ) => {
    const changedKeys = Object.keys(configChanges);
    const allKeys = Object.keys(currentConfig);
    const unchangedKeys = allKeys.filter(k => !changedKeys.includes(k));

    const rows: Array<{
      key: string;
      oldVal: string;
      newVal: string;
      type: 'modified' | 'unchanged';
    }> = [];

    changedKeys.forEach(key => {
      rows.push({
        key,
        oldVal: configChanges[key].old,
        newVal: configChanges[key].new,
        type: 'modified',
      });
    });

    unchangedKeys.forEach(key => {
      rows.push({key, oldVal: currentConfig[key], newVal: currentConfig[key], type: 'unchanged'});
    });

    const visibleRows = rows.filter(r => showUnchanged || r.type !== 'unchanged');
    if (visibleRows.length === 0) return null;

    return (
      <TableContainer component={Paper} variant="outlined" sx={{mb: 2}}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '30%'}}>Property</TableCell>
              <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '35%'}}>Current</TableCell>
              <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '35%'}}>New</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{fontWeight: 500}}>{formatConfigKey(row.key)}</TableCell>
                <TableCell>{row.oldVal}</TableCell>
                <TableCell
                  sx={{
                    bgcolor: (theme) =>
                      row.type === 'modified'
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(25, 118, 210, 0.2)'
                          : '#e3f2fd'
                        : 'transparent',
                    fontWeight: row.type === 'modified' ? 600 : 400,
                  }}
                >
                  {row.newVal}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderModifiedVisit = (visit: any) => {
    const currentVisit = getCurrentVisit(visit.visit_id);
    const currentForms = currentVisit?.forms || [];
    const formsToAdd = visit.forms?.add || [];
    const formsToRemove = visit.forms?.remove || [];
    const formsToModify = visit.forms?.modify || [];

    const configChanges = getVisitConfigChanges(visit);
    const currentConfig = currentVisit ? getVisitConfig(currentVisit) : {};
    const configChangeCount = Object.keys(configChanges).length;
    const hasFormChanges = formsToAdd.length > 0 || formsToRemove.length > 0 || formsToModify.length > 0;

    const formRows: Array<{
      current: any;
      new: any;
      type: 'unchanged' | 'added' | 'removed' | 'modified';
    }> = [];

    currentForms.forEach((form: any) => {
      const isRemoved = formsToRemove.some((f: any) => f.name === form.name);
      const isModified = formsToModify.some((f: any) => f.name === form.name);

      if (!isRemoved && !isModified) {
        formRows.push({current: form, new: null, type: 'unchanged'});
      }
    });

    formsToRemove.forEach((form: any) => {
      formRows.push({current: form, new: form, type: 'removed'});
    });

    formsToModify.forEach((newForm: any) => {
      const oldForm = currentForms.find((f: any) => f.name === newForm.name);
      formRows.push({current: oldForm, new: newForm, type: 'modified'});
    });

    formsToAdd.forEach((form: any) => {
      formRows.push({current: null, new: form, type: 'added'});
    });

    const currentWindow = currentConfig.visit_window;
    const windowDisplay = configChanges.visit_window
      ? `${configChanges.visit_window.old} → ${configChanges.visit_window.new}`
      : currentWindow || '';

    return (
      <Box key={visit.visit_id} sx={{mb: 3, ml: 2}}>
        <Typography variant="h6" sx={{mb: 1, fontWeight: 600}}>
          {visit.visit_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
          Visit ID: {visit.visit_id}
          {windowDisplay && ` • Window: ${windowDisplay}`}
        </Typography>

        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
          {configChangeCount > 0 && (
            <Chip icon={<SettingsIcon />} label={`${configChangeCount} Config`} color="warning" size="small" />
          )}
          {formsToAdd.length > 0 && (
            <Chip icon={<AddIcon />} label={`${formsToAdd.length} Added`} color="success" size="small" />
          )}
          {formsToRemove.length > 0 && (
            <Chip icon={<RemoveIcon />} label={`${formsToRemove.length} Removed`} color="error" size="small" />
          )}
          {formsToModify.length > 0 && (
            <Chip icon={<EditIcon />} label={`${formsToModify.length} Modified`} color="info" size="small" />
          )}
        </Box>

        {configChangeCount > 0 && (
          <Box sx={{mb: 2}}>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              Visit Configuration Changes
            </Typography>
            {renderConfigChangesTable(configChanges, currentConfig)}
          </Box>
        )}

        {(hasFormChanges || showUnchanged) && formRows.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              Forms
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                    <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formRows
                    .filter(row => showUnchanged || row.type !== 'unchanged')
                    .map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          {row.current ? renderFormCell(row.current, 'current') : (
                            <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            p: 2,
                            bgcolor: (theme) => {
                              if (theme.palette.mode === 'dark') {
                                return row.type === 'added'
                                  ? 'rgba(46, 125, 50, 0.2)'
                                  : row.type === 'removed'
                                    ? 'rgba(211, 47, 47, 0.2)'
                                    : row.type === 'modified'
                                      ? 'rgba(25, 118, 210, 0.2)'
                                      : 'transparent';
                              }
                              return row.type === 'added'
                                ? '#e8f5e9'
                                : row.type === 'removed'
                                  ? '#ffebee'
                                  : row.type === 'modified'
                                    ? '#e3f2fd'
                                    : 'transparent';
                            },
                          }}
                        >
                          {row.new ? renderFormCell(row.new, row.type) : (
                            <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };

  // Render removed visit
  const renderRemovedVisit = (visit: any) => {
    const currentVisit = getCurrentVisit(visit.visit_id);

    if (!currentVisit) {
      return null;
    }

    // Ensure forms is an array
    const forms = Array.isArray(currentVisit.forms) ? currentVisit.forms : [];

    return (
      <Box key={visit.visit_id} sx={{mb: 3, ml: 2}}>
        <Paper variant="outlined" sx={{p: 2, border: 1, borderColor: 'divider'}}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: 'error.main',
              textDecoration: 'line-through',
            }}
          >
            🗑️ Visit: {visit.visit_name || visit.visit_id}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
            This entire visit will be removed from the protocol
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length > 0 ? forms.map((form: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{verticalAlign: 'top', p: 2}}>
                      {renderFormCell(form, 'current')}
                    </TableCell>
                    <TableCell
                      sx={{
                        verticalAlign: 'top',
                        p: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee',
                      }}
                    >
                      {renderFormCell(form, 'removed')}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{textAlign: 'center', p: 2}}>
                      <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        No forms in this visit
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // Render added visit
  const renderAddedVisit = (visit: any) => {
    // Handle both array and object format for forms
    let forms: any[] = [];
    if (Array.isArray(visit.forms)) {
      forms = visit.forms;
    } else if (visit.forms && typeof visit.forms === 'object') {
      // If forms is an object with add/remove/modify structure
      forms = visit.forms.add || [];
    }

    return (
      <Box key={visit.visit_id} sx={{mb: 3, ml: 2}}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#e8f5e9',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.5)' : '#4caf50',
          }}
        >
          <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1, color: 'success.main'}}>
            ➕ Visit: {visit.visit_name || visit.visit_id}
          </Typography>
          {visit.visit_window && (
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
              Window: {visit.visit_window}
            </Typography>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length > 0 ? forms.map((form: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{verticalAlign: 'top', p: 2}}>
                      <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        verticalAlign: 'top',
                        p: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#e8f5e9',
                      }}
                    >
                      {renderFormCell(form, 'added')}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{textAlign: 'center', p: 2}}>
                      <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        No forms in this visit
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // Render unchanged visit
  const renderUnchangedVisit = (visit: any) => {
    // Ensure forms is an array
    const forms = Array.isArray(visit.forms) ? visit.forms : [];

    return (
      <Box key={visit.visit_id} sx={{mb: 3, ml: 2}}>
        <Paper variant="outlined" sx={{p: 2, border: 1, borderColor: 'divider'}}>
          <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
            Visit: {visit.visit_name || visit.visit_id}
          </Typography>
          {visit.visit_window && (
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
              Window: {visit.visit_window}
            </Typography>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                  <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length > 0 ? forms.map((form: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell sx={{verticalAlign: 'top', p: 2}}>
                      {renderFormCell(form, 'current')}
                    </TableCell>
                    <TableCell sx={{verticalAlign: 'top', p: 2}}>
                      <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{textAlign: 'center', p: 2}}>
                      <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        No forms in this visit
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  const renderScheduleFormChange = (form: any, type: 'added' | 'removed' | 'modified') => {
    const changedFields: Array<{label: string; old: string; new: string}> = [];

    if (type === 'modified') {
      if (form.old_timing_rule !== form.new_timing_rule) {
        changedFields.push({label: 'Timing Rule', old: form.old_timing_rule, new: form.new_timing_rule});
      }
      if (form.old_administrator !== form.new_administrator) {
        changedFields.push({label: 'Administrator', old: form.old_administrator, new: form.new_administrator});
      }
      if (form.old_type !== form.new_type) {
        changedFields.push({label: 'Type', old: form.old_type, new: form.new_type});
      }
    }

    return (
      <TableRow key={form.name}>
        <TableCell sx={{verticalAlign: 'top', p: 2}}>
          <Typography variant="body1" sx={{fontWeight: 600, mb: 0.5}}>
            {form.name}
          </Typography>
          {type === 'modified' && changedFields.map(f => (
            <Typography key={f.label} variant="body2" color="text.secondary" sx={{mb: 0.5}}>
              {f.label}: {f.old}
            </Typography>
          ))}
          {type === 'removed' && (
            <Typography variant="body2" color="text.secondary">
              Type: {form.type || form.old_type} • Admin: {form.administrator || form.old_administrator}
            </Typography>
          )}
        </TableCell>
        <TableCell
          sx={{
            verticalAlign: 'top',
            p: 2,
            bgcolor: (theme) => {
              if (theme.palette.mode === 'dark') {
                return type === 'added'
                  ? 'rgba(46, 125, 50, 0.2)'
                  : type === 'removed'
                    ? 'rgba(211, 47, 47, 0.2)'
                    : 'rgba(25, 118, 210, 0.2)';
              }
              return type === 'added' ? '#e8f5e9' : type === 'removed' ? '#ffebee' : '#e3f2fd';
            },
          }}
        >
          {type === 'removed' ? (
            <Typography variant="body1" sx={{fontWeight: 600, textDecoration: 'line-through', color: 'error.main'}}>
              {form.name}
            </Typography>
          ) : (
            <>
              <Typography variant="body1" sx={{fontWeight: 600, mb: 0.5}}>
                {form.name}
              </Typography>
              {type === 'modified' && changedFields.map(f => (
                <Typography key={f.label} variant="body2" sx={{mb: 0.5, fontWeight: 600}}>
                  {f.label}: {f.new}
                </Typography>
              ))}
              {type === 'added' && (
                <Typography variant="body2" color="text.secondary">
                  Type: {form.type || form.new_type} • Admin: {form.administrator || form.new_administrator}
                </Typography>
              )}
            </>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderScheduleSection = () => {
    const schedule = comparison?.changes?.schedule;
    if (!schedule) {
      return (
        <Typography color="text.secondary" sx={{p: 2}}>
          No schedule changes
        </Typography>
      );
    }

    const visits = schedule.visits || {};
    const modifiedVisits = visits.modify || [];
    const addedVisits = visits.add || [];
    const removedVisits = visits.remove || [];

    const forms = schedule.forms || {};
    const formsAdded = forms.add || [];
    const formsRemoved = forms.remove || [];
    const formsModified = forms.modify || [];
    const hasFormChanges = formsAdded.length > 0 || formsRemoved.length > 0 || formsModified.length > 0;

    const unchangedVisits: any[] = [];
    if (showUnchanged) {
      const currentVisits = comparison?.current_state?.schedule?.visits || [];
      const changedVisitIds = [
        ...modifiedVisits.map((v: any) => v.visit_id),
        ...addedVisits.map((v: any) => v.visit_id),
        ...removedVisits.map((v: any) => v.visit_id),
      ];

      currentVisits.forEach((visit: any) => {
        if (!changedVisitIds.includes(visit.visit_id)) {
          unchangedVisits.push(visit);
        }
      });
    }

    const hasVisitChanges = modifiedVisits.length > 0 || addedVisits.length > 0 || removedVisits.length > 0;

    return (
      <Box sx={{p: 2}}>
        {/* Schedule-level form changes */}
        {hasFormChanges && (
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
              Form Changes ({formsAdded.length + formsRemoved.length + formsModified.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ml: 2}}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                    <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formsRemoved.map((form: any) => renderScheduleFormChange(form, 'removed'))}
                  {formsModified.map((form: any) => renderScheduleFormChange(form, 'modified'))}
                  {formsAdded.map((form: any) => renderScheduleFormChange(form, 'added'))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Removed Visits */}
        {removedVisits.length > 0 && (
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'error.main'}}>
              Removed Visits ({removedVisits.length})
            </Typography>
            {removedVisits.map((visit: any) => renderRemovedVisit(visit))}
          </Box>
        )}

        {/* Modified Visits */}
        {modifiedVisits.length > 0 && (
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
              Modified Visits ({modifiedVisits.length})
            </Typography>
            {modifiedVisits.map((visit: any) => renderModifiedVisit(visit))}
          </Box>
        )}

        {/* Added Visits */}
        {addedVisits.length > 0 && (
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'success.main'}}>
              Added Visits ({addedVisits.length})
            </Typography>
            {addedVisits.map((visit: any) => renderAddedVisit(visit))}
          </Box>
        )}

        {/* Unchanged Visits (only when toggle is ON) */}
        {unchangedVisits.length > 0 && (
          <Box sx={{mb: 3}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, color: 'text.secondary'}}>
              Unchanged Visits ({unchangedVisits.length})
            </Typography>
            {unchangedVisits.map((visit: any) => renderUnchangedVisit(visit))}
          </Box>
        )}

        {!hasVisitChanges && !hasFormChanges && (
          <Typography color="text.secondary">
            No schedule changes found
          </Typography>
        )}
      </Box>
    );
  };

  // Truncate text to max 2 lines
  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle context dialog
  const handleContextClick = (request: string, context: string) => {
    setSelectedContext({request, context});
    setContextDialogOpen(true);
  };

  // Render app functionality cell
  const renderAppFunctionalityCell = (item: any, type: 'current' | 'new' | 'added' | 'removed' | 'modified' | 'unchanged') => {
    if (!item) {
      return <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>;
    }

    const isRemoved = type === 'removed';

    return (
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 1,
            textDecoration: isRemoved ? 'line-through' : 'none',
            color: isRemoved ? 'error.main' : 'inherit',
          }}
        >
          Request: {item.request}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: isRemoved ? 'line-through' : 'none',
          }}
        >
          {truncateText(item.context)}
        </Typography>
        <Button
          size="small"
          onClick={() => handleContextClick(item.request, item.context)}
          sx={{textTransform: 'none', p: 0, minWidth: 'auto'}}
        >
          Click to expand
        </Button>
      </Box>
    );
  };

  // Render App Functionalities section
  const renderAppFunctionalitiesSection = () => {
    if (!comparison?.changes?.app_functionalities) {
      return (
        <Typography color="text.secondary" sx={{p: 2}}>
          No app functionality changes
        </Typography>
      );
    }

    const appFuncs = comparison.changes.app_functionalities;
    const added = appFuncs.added || [];
    const removed = appFuncs.removed || [];
    const modified = appFuncs.modified || [];

    // Get current state
    const currentAppFuncs = comparison?.current_state?.app_functionalities || [];

    // Group by area_impact
    const groupedChanges: Record<string, {added: any[], removed: any[], modified: any[], unchanged: any[]}> = {};

    // Process changes
    [...added, ...removed, ...modified].forEach((item: any) => {
      const area = item.area_impact;
      if (!groupedChanges[area]) {
        groupedChanges[area] = {added: [], removed: [], modified: [], unchanged: []};
      }

      if (added.includes(item)) groupedChanges[area].added.push(item);
      if (removed.includes(item)) groupedChanges[area].removed.push(item);
      if (modified.includes(item)) groupedChanges[area].modified.push(item);
    });

    // Add unchanged items if toggle is ON
    if (showUnchanged) {
      const changedRequests = new Set([
        ...added.map((i: any) => i.request),
        ...removed.map((i: any) => i.request),
        ...modified.map((i: any) => i.request),
      ]);

      currentAppFuncs.forEach((item: any) => {
        if (!changedRequests.has(item.request)) {
          const area = item.area_impact;
          if (!groupedChanges[area]) {
            groupedChanges[area] = {added: [], removed: [], modified: [], unchanged: []};
          }
          groupedChanges[area].unchanged.push(item);
        }
      });
    }


    return (
      <Box sx={{p: 2}}>
        {Object.entries(groupedChanges).map(([areaImpact, items]) => {
          const totalChanges = items.added.length + items.removed.length + items.modified.length;

          return (
            <Box key={areaImpact} sx={{mb: 3}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
                {areaImpact} ({totalChanges} change{totalChanges !== 1 ? 's' : ''})
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ml: 2}}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                      <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Removed items */}
                    {items.removed.map((item: any, idx: number) => {
                      const currentItem = currentAppFuncs.find((c: any) => c.request === item.request);
                      return (
                        <TableRow key={`removed-${idx}`}>
                          <TableCell sx={{verticalAlign: 'top', p: 2}}>
                            {renderAppFunctionalityCell(currentItem || item, 'current')}
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              p: 2,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee',
                            }}
                          >
                            {renderAppFunctionalityCell(item, 'removed')}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Modified items */}
                    {items.modified.map((item: any, idx: number) => {
                      const currentItem = currentAppFuncs.find((c: any) => c.request === item.request);
                      return (
                        <TableRow key={`modified-${idx}`}>
                          <TableCell sx={{verticalAlign: 'top', p: 2}}>
                            {renderAppFunctionalityCell(currentItem, 'current')}
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              p: 2,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : '#e3f2fd',
                            }}
                          >
                            {renderAppFunctionalityCell(item, 'modified')}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Added items */}
                    {items.added.map((item: any, idx: number) => (
                      <TableRow key={`added-${idx}`}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            p: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#e8f5e9',
                          }}
                        >
                          {renderAppFunctionalityCell(item, 'added')}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Unchanged items (when toggle is ON) */}
                    {items.unchanged.map((item: any, idx: number) => (
                      <TableRow key={`unchanged-${idx}`}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          {renderAppFunctionalityCell(item, 'current')}
                        </TableCell>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}

        {/* Show message if no changes */}
        {Object.keys(groupedChanges).length === 0 && (
          <Typography color="text.secondary">
            No app functionality changes found
          </Typography>
        )}
      </Box>
    );
  };

  // Count app functionality changes
  const countAppFunctionalityChanges = () => {
    const appFuncs = comparison?.changes?.app_functionalities;
    if (!appFuncs) return 0;

    const added = appFuncs.added?.length || 0;
    const removed = appFuncs.removed?.length || 0;
    const modified = appFuncs.modified?.length || 0;

    return added + removed + modified;
  };

  const renderCustomRequirementCell = (item: any, type: 'current' | 'new' | 'added' | 'removed' | 'modified') => {
    if (!item) {
      return <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>;
    }

    const isRemoved = type === 'removed';
    const reqItem = item.item || item;

    return (
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            textDecoration: isRemoved ? 'line-through' : 'none',
            color: isRemoved ? 'error.main' : 'inherit',
          }}
        >
          {reqItem.request || item.request || '---'}
        </Typography>
        {reqItem.area_impact && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Area: {reqItem.area_impact}
          </Typography>
        )}
        {reqItem.applicable_to && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Platform: {reqItem.applicable_to}
          </Typography>
        )}
        {reqItem.context_of_feature && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mb: 0.5,
              }}
            >
              {truncateText(reqItem.context_of_feature)}
            </Typography>
            <Button
              size="small"
              onClick={() => handleContextClick(
                reqItem.request || item.request,
                reqItem.context_of_feature,
              )}
              sx={{textTransform: 'none', p: 0, minWidth: 'auto'}}
            >
              Click to expand
            </Button>
          </>
        )}
      </Box>
    );
  };

  const renderModifiedCustomRequirement = (item: any) => {
    const changes = item.field_changes || {};
    const changedFields = Object.keys(changes);

    return (
      <Box>
        <Typography variant="body1" sx={{fontWeight: 600, mb: 0.5}}>
          {item.request}
        </Typography>
        {item.area_impact && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Area: {item.area_impact}
          </Typography>
        )}
        {changedFields.map(field => (
          <Box key={field} sx={{mt: 1, pl: 1, borderLeft: '3px solid', borderColor: 'info.main'}}>
            <Typography variant="caption" sx={{fontWeight: 600, textTransform: 'capitalize'}}>
              {formatConfigKey(field)}:
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {truncateText(String(changes[field].new), 200)}
            </Typography>
            <Button
              size="small"
              onClick={() => handleContextClick(
                `${item.request} — ${formatConfigKey(field)}`,
                `OLD:\n${changes[field].old}\n\nNEW:\n${changes[field].new}`,
              )}
              sx={{textTransform: 'none', p: 0, minWidth: 'auto'}}
            >
              Click to expand
            </Button>
          </Box>
        ))}
      </Box>
    );
  };

  const renderCustomRequirementsSection = () => {
    const customReqs = comparison?.changes?.custom_requirements;
    if (!customReqs) {
      return (
        <Typography color="text.secondary" sx={{p: 2}}>
          No custom requirements changes
        </Typography>
      );
    }

    const added = customReqs.added || [];
    const removed = customReqs.removed || [];
    const modified = customReqs.modified || [];

    const groupedChanges: Record<string, {added: any[]; removed: any[]; modified: any[]}> = {};

    [...added, ...removed, ...modified].forEach((item: any) => {
      const section = item.section || item.area_impact || 'Other';
      if (!groupedChanges[section]) {
        groupedChanges[section] = {added: [], removed: [], modified: []};
      }
      if (added.includes(item)) groupedChanges[section].added.push(item);
      if (removed.includes(item)) groupedChanges[section].removed.push(item);
      if (modified.includes(item)) groupedChanges[section].modified.push(item);
    });

    return (
      <Box sx={{p: 2}}>
        {Object.entries(groupedChanges).map(([section, items]) => {
          const totalChanges = items.added.length + items.removed.length + items.modified.length;

          return (
            <Box key={section} sx={{mb: 3}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
                {section} ({totalChanges} change{totalChanges !== 1 ? 's' : ''})
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ml: 2}}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>Current</TableCell>
                      <TableCell sx={{fontWeight: 600, bgcolor: 'action.hover', width: '50%'}}>New</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.removed.map((item: any, idx: number) => (
                      <TableRow key={`removed-${idx}`}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          {renderCustomRequirementCell(item, 'current')}
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            p: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffebee',
                          }}
                        >
                          {renderCustomRequirementCell(item, 'removed')}
                        </TableCell>
                      </TableRow>
                    ))}
                    {items.modified.map((item: any, idx: number) => (
                      <TableRow key={`modified-${idx}`}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          {renderCustomRequirementCell(item, 'current')}
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            p: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : '#e3f2fd',
                          }}
                        >
                          {renderModifiedCustomRequirement(item)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {items.added.map((item: any, idx: number) => (
                      <TableRow key={`added-${idx}`}>
                        <TableCell sx={{verticalAlign: 'top', p: 2}}>
                          <Box sx={{color: 'text.secondary', fontStyle: 'italic'}}>---</Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            verticalAlign: 'top',
                            p: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : '#e8f5e9',
                          }}
                        >
                          {renderCustomRequirementCell(item, 'added')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}

        {Object.keys(groupedChanges).length === 0 && (
          <Typography color="text.secondary">
            No custom requirements changes found
          </Typography>
        )}
      </Box>
    );
  };

  const countCustomRequirementsChanges = () => {
    const customReqs = comparison?.changes?.custom_requirements;
    if (!customReqs) return 0;

    return (customReqs.added?.length || 0) +
           (customReqs.removed?.length || 0) +
           (customReqs.modified?.length || 0);
  };

  if (!comparisonData) {
    return (
      <Box sx={{p: 2, bgcolor: 'action.hover', borderRadius: 1}}>
        <Typography variant="body2" color="text.secondary">
          No comparison data available
        </Typography>
      </Box>
    );
  }

  const scheduleChangeCount = countScheduleChanges();
  const appFunctionalityChangeCount = countAppFunctionalityChanges();
  const customRequirementsChangeCount = countCustomRequirementsChanges();

  return (
    <Box>
      {/* Header */}
      <Box sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <Box>
          <Typography variant="h6" sx={{fontWeight: 600}}>
            PRS Comparison: {comparison.prs_versions?.current} → {comparison.prs_versions?.new}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Analyzed: {new Date(comparison.analyzed_at).toLocaleString()}
          </Typography>
        </Box>

        {/* Toggle to show/hide unchanged items */}
        <FormControlLabel
          control={
            <Switch
              checked={showUnchanged}
              onChange={(e) => setShowUnchanged(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Show unchanged
            </Typography>
          }
        />
      </Box>

      <Divider sx={{mb: 2}} />

      {comparison.summary && (
        <Box sx={{mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1}}>
          <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
            Summary
          </Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <Chip label={`${comparison.summary.total_changes} Total Changes`} size="small" />
            {(comparison.summary.visits_modified > 0) && (
              <Chip label={`${comparison.summary.visits_modified} Visits Modified`} size="small" color="warning" />
            )}
            {comparison.summary.visits_added > 0 && (
              <Chip label={`${comparison.summary.visits_added} Visits Added`} size="small" color="success" />
            )}
            {comparison.summary.visits_removed > 0 && (
              <Chip label={`${comparison.summary.visits_removed} Visits Removed`} size="small" color="error" />
            )}
            {comparison.summary.forms_added > 0 && (
              <Chip label={`${comparison.summary.forms_added} Forms Added`} size="small" color="success" />
            )}
            {comparison.summary.forms_removed > 0 && (
              <Chip label={`${comparison.summary.forms_removed} Forms Removed`} size="small" color="error" />
            )}
            {comparison.summary.forms_modified > 0 && (
              <Chip label={`${comparison.summary.forms_modified} Forms Modified`} size="small" color="info" />
            )}
            {comparison.summary.custom_requirements_added > 0 && (
              <Chip label={`${comparison.summary.custom_requirements_added} Custom Req Added`} size="small" color="success" />
            )}
            {comparison.summary.custom_requirements_removed > 0 && (
              <Chip label={`${comparison.summary.custom_requirements_removed} Custom Req Removed`} size="small" color="error" />
            )}
            {comparison.summary.custom_requirements_modified > 0 && (
              <Chip label={`${comparison.summary.custom_requirements_modified} Custom Req Modified`} size="small" color="info" />
            )}
          </Box>
        </Box>
      )}

      {/* Schedule Section */}
      <Accordion
        expanded={expandedSections.includes('schedule')}
        onChange={() => handleSectionToggle('schedule')}
        sx={{
          border: 1, borderColor: 'divider',
          '&:before': {display: 'none'},
          boxShadow: 1,
          mb: 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'action.hover',
            '&:hover': {bgcolor: 'action.hover'},
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%'}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
              Schedule
            </Typography>
            {scheduleChangeCount > 0 && (
              <Chip
                label={`${scheduleChangeCount} change${scheduleChangeCount !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {renderScheduleSection()}
        </AccordionDetails>
      </Accordion>

      {/* App Functionalities Section */}
      {comparison?.changes?.app_functionalities && (
        <Accordion
          expanded={expandedSections.includes('app_functionalities')}
          onChange={() => handleSectionToggle('app_functionalities')}
          sx={{
            border: 1, borderColor: 'divider',
            '&:before': {display: 'none'},
            boxShadow: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': {bgcolor: 'action.hover'},
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%'}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                App Functionalities
              </Typography>
              {appFunctionalityChangeCount > 0 && (
                <Chip
                  label={`${appFunctionalityChangeCount} change${appFunctionalityChangeCount !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderAppFunctionalitiesSection()}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Custom Requirements Section */}
      {comparison?.changes?.custom_requirements && (
        <Accordion
          expanded={expandedSections.includes('custom_requirements')}
          onChange={() => handleSectionToggle('custom_requirements')}
          sx={{
            border: 1, borderColor: 'divider',
            '&:before': {display: 'none'},
            boxShadow: 1,
            mb: 2,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': {bgcolor: 'action.hover'},
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%'}}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                Custom Requirements
              </Typography>
              {customRequirementsChangeCount > 0 && (
                <Chip
                  label={`${customRequirementsChangeCount} change${customRequirementsChangeCount !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderCustomRequirementsSection()}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Context Expansion Dialog */}
      <Dialog
        open={contextDialogOpen}
        onClose={() => setContextDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography variant="h6">
            {selectedContext?.request}
          </Typography>
          <IconButton onClick={() => setContextDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>
            {selectedContext?.context}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContextDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Future sections: Eligibility, Endpoints, etc. */}
    </Box>
  );
};
